import { Button, Card, DatePicker } from "@devhop/ui";
import { config } from "@/shared/lib/config";
import { formatDateForInput } from "@/shared/lib/date-time";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";

type DepartmentItem = { id: string; name: string };
type ChecklistLookupItem = { id: string; name: string };

type ChecklistResultFilterProps = {
  departmentId: string;
  checklistId: string;
  startDate: string;
  endDate: string;
  onDepartmentChange: (value: string) => void;
  onChecklistChange: (value: string) => void;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onFilterChange: () => void;
};

export function ChecklistResultFilter({
  departmentId,
  checklistId,
  startDate,
  endDate,
  onDepartmentChange,
  onChecklistChange,
  onStartDateChange,
  onEndDateChange,
  onFilterChange,
}: ChecklistResultFilterProps) {
  const handleDateRangeChange = (
    range:
      | {
          from?: Date;
          to?: Date;
        }
      | Date[]
      | undefined,
  ) => {
    const from: Date | undefined = Array.isArray(range)
      ? range[0]
      : range?.from;
    const to: Date | undefined = Array.isArray(range) ? range[1] : range?.to;
    const fromStr = from ? formatDateForInput(from) : "";
    const toStr = to ? formatDateForInput(to) : "";
    onStartDateChange(fromStr);
    onEndDateChange(toStr);
  };

  return (
    <Card className="p-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="mb-1 font-medium text-sm">ພະແນກ</div>
          <InfiniteCombobox<DepartmentItem>
            queryKey={["checklists-result-departments"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/checklists/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/checklists/departments/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={departmentId}
            onValueChange={(v) => {
              onDepartmentChange(v);
              onChecklistChange("");
            }}
            placeholder="ທຸກພະແນກ"
            clearable
          />
        </div>
        <div>
          <div className="mb-1 font-medium text-sm">Checklist</div>
          <InfiniteCombobox<ChecklistLookupItem>
            queryKey={["checklists-result-checklists", departmentId]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/checklists/lookup`,
                args,
                departmentId ? { departmentId } : undefined,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/checklists/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={checklistId}
            onValueChange={onChecklistChange}
            placeholder="ທຸກ Checklist"
            clearable
          />
        </div>
        <div>
          <div className="mb-1 font-medium text-sm">ຊ່ວງວັນທີ (ເລີ່ມບັນທຶກ)</div>
          <DatePicker
            mode="range"
            value={{
              from: startDate ? new Date(startDate) : undefined,
              to: endDate ? new Date(endDate) : undefined,
            }}
            onChange={handleDateRangeChange}
            placeholder="ເລືອກຊ່ວງວັນທີ"
            className="w-full"
          />
        </div>
        <div className="flex items-end">
          <Button onClick={onFilterChange} className="w-full">
            ກວດສອບ
          </Button>
        </div>
      </div>
    </Card>
  );
}
